import React from "react";
import {observer} from "mobx-react";

import "./time-series-range-selector.scss";

import {Header, Icon, Dropdown, Divider, Form, Message, Popup, List} from "semantic-ui-react";
import SemanticDatepicker from "react-semantic-ui-datepickers";

import dayjs = require("dayjs");
import "dayjs/locale/de-ch";

import {SelectOptionConfiguration} from "../../../../../../../core/config/select-option-configuration";
import {TimeSeries} from "../../../../../../../kiwis";
import {computed} from "mobx";
import {TimeSeriesRangeSelectorConfiguration} from "../../../../../../../config/chart-configuration";


interface TimeSeriesRangeSelectorProps {
    periods: SelectOptionConfiguration[];
    defaultPeriod: string;
    timeSeries: TimeSeries;
    parentWidth: number;
    config: TimeSeriesRangeSelectorConfiguration;
}

dayjs.locale("de-ch");

@observer
export class TimeSeriesRangeSelector extends React.Component<TimeSeriesRangeSelectorProps, any> {
    constructor(props: TimeSeriesRangeSelectorProps) {
        super(props);
        this.state = {
            loading: true,
            error: null
        };
    }

    /**
     * period should not be longer than 2 weeks to avoid very long requests and time outs
     *
     * @param from
     * @param to
     */
    isValidDateRange(from: Date, to: Date): boolean {
        if (from && to && (to.getTime() - from.getTime() > 1.21e+9)) {
            this.setState({
                error: "Zeitperiode zu lang. Die Abfrage an KIWIS würde sehr lange dauern."
            });
            return false;
        }
        this.setState({
            error: null
        });
        return true;
    }

    /**
     * Return date to display initially
     *
     * @param ts
     * @param type
     */
    initialDate(ts: TimeSeries, type: string) {
        if (type === "from") {
            // return selected from date if available
            if (ts.from) {
                return new Date(ts.from);
            }
            // otherwise initially start with the 'to' date
            if (ts.to) {
                return new Date(ts.to);
            }
            // fallback
            return new Date();
        }
        else if (type === "to") {
            // 'to' date should always be set initially, as soon as data is available
            if (ts.to) {
                return new Date(ts.to);
            }
            // otherwise take the last date of the available data range
            if (this.props.timeSeries.selectedDateRange && this.props.timeSeries.selectedDateRange.to) {
                return new Date(this.props.timeSeries.selectedDateRange.to);
            }
            // fallback
            return new Date();
        }
    }

    @computed
    get periodDropdownValue() {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const defaultPeriod = this.props.defaultPeriod[this.props.timeSeries.stationParameterName];
        return this.props.timeSeries.selectedDateRange ? this.props.timeSeries.selectedDateRange.iso : defaultPeriod;
    }

    componentDidMount() {
        this.setState({loading: false});
    }

    componentWillUnmount() {
        // TODO remove watches
    }

    render() {
        const {timeSeries, periods} = this.props;
        const showFromTo = !this.props.config.onlyPeriod;

        const periodSelect =
            <Dropdown floating={false}
                placeholder={"Zeiperiode"}
                options={periods}
                direction={"left"}
                selection={true}
                additionPosition={"top"}
                defaultValue={this.periodDropdownValue}
                onChange={(event, data) => {
                    timeSeries.setPeriodISO(String(data.value));
                }} />;

        let fromDate;
        let toDate;
        if (showFromTo) {
            fromDate = (
                <SemanticDatepicker
                    disabled={true}
                    locale={"de-DE"}
                    onChange={(event: React.SyntheticEvent | undefined, date: any) => {
                        if (date instanceof Date) {
                            if (!this.isValidDateRange(date, new Date(timeSeries.to))) {
                                return;
                            }
                            timeSeries.from = dayjs(date).format();
                        }
                    }}
                    value={timeSeries.selectedDateRange ?
                        timeSeries.selectedDateRange.fromAsDate :
                        undefined}
                    date={this.initialDate(timeSeries, "from")}
                    maxDate={new Date(timeSeries.to)}
                    type="basic"
                    pointing={"top left"}
                    format={"DD-MM-YYYY"}
                    clearable={true}
                    placeholder={"von ..."}
                />
            );
            toDate = (
                <SemanticDatepicker
                    disabled={true}
                    locale={"de-DE"}
                    onChange={(event: React.SyntheticEvent | undefined, date: any) => {
                        if (date instanceof Date) {
                            if (!this.isValidDateRange(new Date(timeSeries.from), date)) {
                                return;
                            }
                            timeSeries.to = dayjs(date).format();
                        }
                    }}
                    value={timeSeries.selectedDateRange ?
                        timeSeries.selectedDateRange.toAsDate :
                        undefined}
                    date={this.initialDate(timeSeries, "to")}
                    minDate={new Date(timeSeries.from)}
                    type="basic"
                    pointing={"top right"}
                    format={"DD-MM-YYYY"}
                    clearable={true}
                    placeholder={"... bis"}
                />
            );
        }

        let fromField;
        let toField;
        if (this.props.parentWidth > 650 && fromDate && toDate) {
            fromField = (<Form.Field inline className={"ts-period-select ts-date-select ts-from-date-select"}>
                {fromDate}
            </Form.Field>);
            toField = (<Form.Field inline className={"ts-period-select ts-date-select"}>
                {toDate}
            </Form.Field>);
        }

        let message;
        if (this.state.error) {
            message = <Message attached="bottom" error className={"date-range-error"}>
                <Icon name="exclamation" />
                {this.state.error}
            </Message>;
        }

        let divider;
        if (showFromTo) {
            divider = (<Divider horizontal>
                <Popup
                    key={`period-popup-${timeSeries.id}`}
                    basic
                    className={"time-series-range-selector-popup"}
                    trigger={<Header as="h6">
                        <Icon name="calendar alternate outline" />
                        Zeitperiode, Start und/oder Enddatum
                    </Header>}>
                    <Popup.Header content={"Zeitperiode bestimmen"}/>
                    <Popup.Content>
                        Die Zeitperiode der Messreihe kann durch folgende Kombinationen bestimmt werden
                        <List bulleted>
                            <List.Item>Periode und Enddatum</List.Item>
                            <List.Item>Periode und Startdatum</List.Item>
                            <List.Item>Startdatum und Enddatum (Periode wird ignoriert)</List.Item>
                        </List>
                    </Popup.Content>
                </Popup>
            </Divider>);
        }

        return (
            <div className="time-range-container">
                {divider}
                <Form>
                    <Form.Field inline className={"ts-period-select"}>
                        {periodSelect}
                    </Form.Field>
                    {fromField}
                    {toField}
                </Form>
                {message}
            </div>
        );
    }
}
